import { MenuItem } from '@/types';
import { defineStore, acceptHMRUpdate } from 'pinia';
import { i18n } from '@/modules/i18n';
import { ComposerTranslation } from 'vue-i18n';
export const useMenuStore = defineStore('menu', () => {
    const $t = i18n.global.t as ComposerTranslation;
    const items = ref<MenuItem[]>([]);
    const display = ref(false);
    const available = ref(false);
    const visible = computed(() => display.value && available.value);

    const home = () => ({
        key: 'home',
        title: $t('menu.home'),
        to: '/',
        icon: 'pi pi-fw pi-desktop'
    });

    const qualityreport = () => ({
        key: 'qualityreport',
        title: $t('menu.qualityreport'),
        to: '/',
        icon: 'pi pi-fw pi-file-check'
    });

    const monitoringplan = () => ({
        key: 'monitoringplan',
        title: $t('menu.monitoringplan'),
        to: '/monitoringplan',
        icon: 'pi pi-fw pi-book'
    });

    const yearlyreport = (index?: boolean) => ({
        key: 'yearlyreport',
        title: $t('menu.yearlyreport'),
        to: index ? '/' : '/yearlyreport',
        icon: 'pi pi-fw pi-calendar'
    });

    const context = () => ({
        key: 'context',
        title: $t('menu.context'),
        to: '/context',
        icon: 'pi pi-arrow-right-arrow-left'
    });

    const authorities = () => ({
        key: 'authority',
        title: $t('menu.authorities'),
        to: '/authority/lb',
        icon: 'pi pi-building'
    });

    const users = () => ({
        key: 'users',
        title: $t('menu.users'),
        to: '/users',
        icon: 'pi pi-users'
    });

    const certifiedLaboratories = () => ({
        key: 'certlab',
        title: $t('menu.certlabs'),
        to: '/laboratory',
        icon: 'pi pi-verified'
    });

    const watersupplier = () => ({
        key: 'watersupplier',
        title: $t('menu.watersupplier'),
        to: '/watersupplier',
        icon: 'pi pi-warehouse'
    });

    const activities = () => ({
        key: 'act',
        title: $t('menu.activities'),
        to: '/activities',
        icon: 'pi pi-history'
    });

    const wsAreas = () => ({
        key: 'act',
        title: $t('menu.wsarea'),
        to: '/wsarea',
        icon: 'pi pi-sitemap'
    });

    const wsSystems = () => ({
        key: 'act',
        title: $t('menu.wssystem'),
        to: '/wssystem',
        icon: 'pi pi-sitemap'
    });

    const wsObjects = () => ({
        key: 'wsobject',
        title: $t('menu.wsobject'),
        to: '/wsobject',
        icon: 'pi pi-th-large'
    });

    const wsSamplePoint = () => ({
        key: 'act',
        title: $t('menu.wssamplepoint'),
        to: '/wssamplepoint',
        icon: 'pi pi-directions-alt'
    });

    const wsImport = () => ({
        key: 'act',
        title: $t('menu.wsimport'),
        to: '/wsimport',
        icon: 'pi pi-upload'
    });

    const commonRoot = (children: MenuItem[]) => ({
        key: 'common',
        title: $t('menu.common'),
        children
    });

    const wsRoot = (children: MenuItem[]) => ({
        key: 'ws',
        title: $t('menu.ws'),
        children
    });

    const adminRoot = (children: MenuItem[]) => ({
        key: 'root',
        title: $t('menu.admin'),
        children
    });

    function wsItems(canImport: boolean) {
        const items = [wsSystems(), wsObjects(), wsSamplePoint(), wsAreas()];
        if (canImport) {
            items.push(wsImport());
        }
        return wsRoot(items);
    }

    function setHdpMenu(owner: boolean) {
        const adminItems = [
            authorities(),
            certifiedLaboratories(),
            watersupplier()
        ];
        if (owner) {
            adminItems.push(users());
        }
        items.value = [
            commonRoot([qualityreport(), monitoringplan(), yearlyreport()]),
            wsItems(true)
        ];

        items.value.push(adminRoot(adminItems));
        enable();
    }

    function setLabMenu(owner: boolean) {
        const adminItems = [
            authorities(),
            watersupplier(),
            certifiedLaboratories()
        ];
        if (owner) {
            adminItems.push(users());
        }

        items.value = [
            commonRoot([qualityreport()]),
            wsItems(false),
            adminRoot(adminItems)
        ];
        enable();
    }

    function setLabBranchMenu(owner: boolean) {
        const adminItems = [
            authorities(),
            watersupplier(),
            certifiedLaboratories()
        ];
        if (owner) {
            adminItems.push(users());
        }

        items.value = [
            commonRoot([qualityreport()]),
            wsItems(false),
            adminRoot(adminItems)
        ];
        enable();
    }

    function setWspMenu(owner: boolean) {
        const adminItems = [
            authorities(),
            watersupplier(),
            certifiedLaboratories()
        ];
        if (owner) {
            adminItems.push(users());
        }

        items.value = [
            commonRoot([qualityreport(), monitoringplan()]),
            wsItems(false),
            adminRoot(adminItems)
        ];
        enable();
    }

    function setWspBranchMenu(owner: boolean) {
        const adminItems = [
            authorities(),
            watersupplier(),
            certifiedLaboratories()
        ];
        if (owner) {
            adminItems.push(users());
        }

        items.value = [
            commonRoot([qualityreport(), monitoringplan()]),
            wsItems(false),
            adminRoot(adminItems)
        ];
        enable();
    }

    function setOlbMenu() {
        items.value = [commonRoot([yearlyreport(true)]), wsItems(true)];

        items.value.push(
            adminRoot([
                authorities(),
                certifiedLaboratories(),
                watersupplier(),
                users()
            ])
        );
        enable();
    }

    function setAdminMenu() {
        items.value = [commonRoot([home()]), wsItems(true)];

        items.value.push(
            adminRoot([
                context(),
                activities(),
                authorities(),
                certifiedLaboratories(),
                watersupplier(),
                users()
            ])
        );
    }

    function enable() {
        available.value = true;
    }

    function disable() {
        available.value = false;
    }

    function show() {
        display.value = true;
    }

    function hide() {
        display.value = false;
    }

    function toggle() {
        display.value = !display.value;
    }

    return {
        visible,
        available,
        items,
        enable,
        disable,
        show,
        hide,
        toggle,
        setOlbMenu,
        setHdpMenu,
        setLabMenu,
        setLabBranchMenu,
        setWspMenu,
        setWspBranchMenu,
        setAdminMenu
    };
});

if (import.meta.hot) {
    import.meta.hot.accept(
        acceptHMRUpdate(useMenuStore as any, import.meta.hot)
    );
}
